import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, fmtErr, fmtDate } from "@/lib/api";
import { PageHead } from "@/components/desk/DeskLayout";
import { StatusChip } from "@/components/desk/StatusChip";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";

const PROFILE_FIELDS = [["phone", "Phone"], ["city", "City"], ["address", "Address"], ["date_of_birth", "Date of birth"],
  ["organization", "Organisation"], ["designation", "Designation"], ["experience_years", "Experience (yrs)"],
  ["languages", "Languages"], ["portfolio_url", "Portfolio"], ["bio", "Bio"]];

export default function ReportersPage() {
  const [tab, setTab] = useState("pending");
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(null);
  const [reject, setReject] = useState(null);
  const [fb, setFb] = useState("");

  const load = () => api.get("/admin/reporters", { params: { status: tab } }).then((r) => setRows(r.data));
  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab]);

  const decide = async (uid, decision, feedback = "") => {
    try {
      await api.post(`/admin/reporters/${uid}/decision`, { decision, feedback });
      toast.success(decision === "approve" ? "Reporter approved" : "Application rejected");
      setReject(null);
      setFb("");
      load();
    } catch (err) {
      toast.error(fmtErr(err));
    }
  };

  return (
    <div data-testid="reporters-page">
      <PageHead title="Reporter applications" sub="Approve external reporters before they can sign in and submit." />
      <div className="flex gap-1 mb-4">
        {["pending", "active", "rejected"].map((t) => (
          <button key={t} data-testid={`reporters-tab-${t}`} onClick={() => setTab(t)}
            className={`h-9 px-3 text-sm border rounded-sm ${tab === t ? "bg-zinc-900 text-white border-zinc-900" : "bg-white border-zinc-300"}`}>
            {t === "active" ? "approved" : t}
          </button>
        ))}
      </div>
      {rows.length === 0 && <p data-testid="reporters-empty" className="text-zinc-500 bg-white border border-zinc-200 p-8">No {tab} applications.</p>}
      <div className="space-y-3">
        {rows.map((r) => (
          <div key={r.id} data-testid={`reporter-row-${r.id}`} className="bg-white border border-zinc-200">
            <div className="p-4 flex flex-wrap items-center justify-between gap-3">
              <button className="text-left" onClick={() => setOpen(open === r.id ? null : r.id)} data-testid={`reporter-expand-${r.id}`}>
                <p className="font-semibold">{r.name} <span className="font-normal text-zinc-500 text-sm">· {r.email}</span></p>
                <p className="text-xs text-zinc-500">{r.profile?.city} · applied {fmtDate(r.created_at)}</p>
              </button>
              <div className="flex items-center gap-2">
                <StatusChip status={r.status} />
                {r.status !== "active" && <Button size="sm" className="rounded-sm" data-testid={`reporter-approve-${r.id}`} onClick={() => decide(r.id, "approve")}>Approve</Button>}
                {r.status !== "rejected" && <Button size="sm" variant="outline" className="rounded-sm text-red-700" data-testid={`reporter-reject-${r.id}`} onClick={() => setReject(r)}>Reject</Button>}
              </div>
            </div>
            {open === r.id && (
              <dl data-testid={`reporter-profile-${r.id}`} className="border-t border-zinc-200 p-4 grid sm:grid-cols-2 gap-x-6 gap-y-2 text-sm">
                {PROFILE_FIELDS.map(([k, l]) => (
                  <div key={k} className={k === "bio" ? "sm:col-span-2" : ""}>
                    <dt className="text-[11px] uppercase tracking-[0.08em] text-zinc-500">{l}</dt>
                    <dd className="whitespace-pre-line">{String(r.profile?.[k] ?? "") || "—"}</dd>
                  </div>
                ))}
                {r.review_feedback && <div className="sm:col-span-2 text-orange-800">Feedback: {r.review_feedback}</div>}
              </dl>
            )}
          </div>
        ))}
      </div>
      <Dialog open={!!reject} onOpenChange={(o) => !o && setReject(null)}>
        <DialogContent className="bg-white" data-testid="reporter-reject-dialog">
          <DialogHeader><DialogTitle>Reject {reject?.name}</DialogTitle></DialogHeader>
          <Textarea data-testid="reporter-reject-feedback" placeholder="Reason (sent to the applicant)" value={fb} onChange={(e) => setFb(e.target.value)} />
          <DialogFooter>
            <Button data-testid="reporter-reject-confirm" disabled={!fb.trim()} onClick={() => decide(reject.id, "reject", fb)}>Reject application</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
